import React, {
  ReactElement,
  useRef,
  useState,
  DragEvent,
  useEffect,
} from "react";
import { Button as MUButton, Grid } from "@mui/material";
import { DropZone, DropZoneErrorText, DropZoneHintText } from "./styled";
import SimpleImageViewer from "../imageViewer/simple";
import { File as BEFile, isImage } from "../../types/be/file";
import { deleteFile } from "../../api/files";
import { getJson } from "../../utils/http";
import Thumbnail from "./components/thumbnail";
import InProgress from "./components/inProgress";

/* istanbul ignore next */
const Uploader = ({
  filesInitState,
  onFileChange,
  onFileDelete,
  isSubmitted = false,
  errorText = "",
  accept = "*",
  isDisabled = false,
  uploadDisabled,
  refreshData,
  maxAllowed,
  placeHolderText,
  uploadButtonText,
  progressPercentage,
  turnedOff = false,
}: {
  placeHolderText: string;
  uploadButtonText: string;
  filesInitState?: BEFile[];
  onFileChange?: (filesToUpload: File[]) => void;
  isSubmitted?: boolean;
  errorText?: string;
  accept?: string | string[];
  onFileDelete?: (files: File[]) => void;
  isDisabled?: boolean;
  uploadDisabled?: boolean;
  refreshData?: () => void;
  maxAllowed?: number;
  progressPercentage?: number;
  turnedOff?: boolean;
}): ReactElement => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [filesInProgress, setFilesInProgress] = useState<File[]>([]);
  const [isFileDeleting, setIsFileDeleting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<BEFile[] | undefined>(
    filesInitState
  );
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    if (filesInitState) {
      setUploadedFiles(filesInitState);
      setFilesToUpload([]);
      setFilesInProgress([]);
    }
  }, [filesInitState]);

  useEffect(() => {
    return () => {
      setFilesToUpload([]);
      setUploadedFiles([]);
      setIsDragActive(false);
    };
  }, []);

  useEffect(() => {
    if (isSubmitted) {
      setFilesToUpload([]);
      setIsDragActive(false);
    }
  }, [isSubmitted]);

  const handleOnFileChange = (): void => {
    const _filesToUpload: File[] = [];
    if (fileInput && fileInput.current && fileInput.current.files) {
      for (let i = 0; i < fileInput.current.files.length; i++)
        if (isAcceptable(fileInput.current.files[i].type)) {
          _filesToUpload.push(fileInput.current.files[i]);
        }
    }
    if (maxAllowed && _filesToUpload.length > maxAllowed) {
      onFileChange && onFileChange([...filesToUpload, ..._filesToUpload]);
      return;
    }
    if (!filesInitState) {
      setFilesToUpload([...filesToUpload, ..._filesToUpload]);
      onFileChange && onFileChange([...filesToUpload, ..._filesToUpload]);
    } else {
      setFilesInProgress(_filesToUpload);
      onFileChange && onFileChange(_filesToUpload);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    const _filesToUpload: File[] = [];
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (isDisabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        if (isAcceptable(e.dataTransfer.files[i].type)) {
          _filesToUpload.push(e.dataTransfer.files[i]);
        }
      }
    }
    if (!filesInitState) {
      setFilesToUpload([...filesToUpload, ..._filesToUpload]);
      onFileChange && onFileChange([...filesToUpload, ..._filesToUpload]);
    } else {
      setFilesInProgress(_filesToUpload);
      onFileChange && onFileChange(_filesToUpload);
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDeleteFileFromUploadList = (file: File): void => {
    setIsFileDeleting(true);
    const _files = filesToUpload?.filter((f) => f.name !== file.name);
    onFileDelete && onFileDelete(_files);
    setFilesToUpload(_files);
    setIsFileDeleting(false);
  };

  const handleDeleteUploadedFile = (file: BEFile): void => {
    setIsFileDeleting(true);
    deleteFile(String(file.id))
      .then(getJson)
      .then(() => {
        setUploadedFiles((_files) => {
          if (_files) return _files.filter((f) => f.id !== file.id);
          else return undefined;
        });
        setIsFileDeleting(false);
        refreshData && refreshData();
      });
  };

  const isAcceptable = (type: string): boolean => {
    if (accept === "*") return true;
    else if (Array.isArray(accept)) {
      let _isAcceptable = false;
      accept.forEach((acceptable) => {
        if (type.includes(acceptable)) {
          _isAcceptable = true;
        }
      });
      return _isAcceptable;
    } else {
      return type.includes(accept);
    }
  };

  const handleDownloadUploadedFile = (
    e: React.MouseEvent<HTMLDivElement>,
    downloadLink: string
  ): void => {
    e.stopPropagation();
    window.open(downloadLink);
  };

  return (
    <>
      {errorText && (
        <Grid container>
          <Grid item sx={{ pl: 3 }}>
            <DropZoneErrorText variant="body2">{errorText}</DropZoneErrorText>
          </Grid>
        </Grid>
      )}
      <DropZone
        onDrop={handleDrop}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        $isDragActive={isDragActive}
        $isError={!!errorText.length}
        variant="outlined"
        sx={{
          width: "100%",
          minHeight: "200px",
          p: 2,
        }}
        data-testid="dropzone-container"
      >
        <Grid
          container
          height={"100%"}
          spacing={1}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          {!turnedOff && (
            <>
              <Grid item lg={12}>
                <Grid container justifyContent={"center"}>
                  <Grid item>
                    <DropZoneHintText variant="body2">
                      {placeHolderText}
                    </DropZoneHintText>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item lg={12}>
                <Grid container justifyContent={"center"}>
                  <Grid item>
                    <MUButton
                      disabled={isDisabled || uploadDisabled}
                      variant="contained"
                      component="label"
                      size="medium"
                    >
                      {uploadButtonText}
                      <input
                        ref={fileInput}
                        name="file[]"
                        hidden
                        accept={`${accept}/*`}
                        multiple
                        type="file"
                        onChange={() => handleOnFileChange()}
                      />
                    </MUButton>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item lg={12}>
            <Grid container spacing={2} justifyContent={"center"}>
              {!!uploadedFiles?.length &&
                uploadedFiles.map((file: BEFile) => (
                  <Grid item key={file.id}>
                    {isImage(file) ? (
                      <SimpleImageViewer
                        url={`${file.url}/show`}
                        downloadLink={file.url}
                        name={`${file.name}.${file.extension}`}
                        onDelete={() =>
                          !isFileDeleting && handleDeleteUploadedFile(file)
                        }
                        isDisabled={isDisabled}
                      />
                    ) : (
                      <Thumbnail
                        name={`${file.name}.${file.extension}`}
                        onDeleteClick={() =>
                          !isFileDeleting && handleDeleteUploadedFile(file)
                        }
                        onDownloadClick={(e) =>
                          handleDownloadUploadedFile(e, file.url)
                        }
                      />
                    )}
                  </Grid>
                ))}
              {!!filesToUpload.length &&
                filesToUpload.map((file, index) => (
                  <Grid item key={`${file.name}-${index}`}>
                    {file.type.includes("image") ? (
                      <SimpleImageViewer
                        url={URL.createObjectURL(file)}
                        name={`${file.name}`}
                        onDelete={() =>
                          !isFileDeleting &&
                          handleDeleteFileFromUploadList(file)
                        }
                        isDisabled={isDisabled}
                      />
                    ) : (
                      <Thumbnail
                        name={`${file.name}`}
                        onDeleteClick={() =>
                          !isFileDeleting &&
                          handleDeleteFileFromUploadList(file)
                        }
                      />
                    )}
                  </Grid>
                ))}
              {!!filesInProgress.length &&
                filesInProgress.map((file, index) => (
                  <Grid item key={`${file.name}-${index}`}>
                    <InProgress
                      url={URL.createObjectURL(file)}
                      progressPercentage={progressPercentage || null}
                    />
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </Grid>
      </DropZone>
    </>
  );
};

export default Uploader;
